"use client";

import React, { useState, useEffect } from "react";

interface TextCarouselProps {
  texts: string[];
}

// Common logic for rendering styled text
const renderStyledText = (text: string) => {
  const parts = text.split(" ");
  if (parts.length > 1) {
    return (
      <>
        <span className="font-bold text-[#EA2236]">{parts[0]}</span>{" "}
        {parts.slice(1).join(" ")}
      </>
    );
  }
  return text;
};

// Mobile Text Carousel Component
function TextCarouselMobile({ texts }: TextCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const itemHeight = 105; // Fixed height for mobile

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % texts.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [texts.length]);

  const commonTextClasses =
    "text-[48px]/none font-light uppercase text-[#F1F1F1]"; // Mobile text size

  return (
    <div
      className="relative overflow-hidden lg:hidden" // Hidden on large screens and above
      style={{
        height: `${itemHeight}px`,
        maskImage:
          "linear-gradient(to bottom, transparent, black 20%, black 80%, transparent)",
        WebkitMaskImage:
          "linear-gradient(to bottom, transparent, black 20%, black 80%, transparent)",
      }}
    >
      <div
        className="transition-transform duration-700 ease-in-out"
        style={{
          transform: `translateY(-${currentIndex * itemHeight}px)`,
        }}
      >
        {texts.map((text, index) => (
          <div
            key={index}
            className="flex items-center justify-center"
            style={{ height: `${itemHeight}px` }}
          >
            {index === 0 ? (
              <h1 className={commonTextClasses}>{renderStyledText(text)}</h1>
            ) : (
              <p className={commonTextClasses}>{renderStyledText(text)}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Desktop Text Carousel Component
function TextCarouselDesktop({ texts }: TextCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const itemHeight = 85; // Fixed height for desktop

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % texts.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [texts.length]);

  const commonTextClasses =
    "lg:text-[80px]/none text-[48px]/none font-light uppercase text-[#F1F1F1]"; // Desktop text size

  return (
    <div
      className="relative overflow-hidden hidden lg:block" // Hidden on small screens, block on large and above
      style={{
        height: `${itemHeight}px`,
        maskImage:
          "linear-gradient(to bottom, transparent, black 20%, black 80%, transparent)",
        WebkitMaskImage:
          "linear-gradient(to bottom, transparent, black 20%, black 80%, transparent)",
      }}
    >
      <div
        className="transition-transform duration-700 ease-in-out"
        style={{
          transform: `translateY(-${currentIndex * itemHeight}px)`,
        }}
      >
        {texts.map((text, index) => (
          <div
            key={index}
            className="flex items-center justify-center"
            style={{ height: `${itemHeight}px` }}
          >
            {index === 0 ? (
              <h1 className={commonTextClasses}>{renderStyledText(text)}</h1>
            ) : (
              <p className={commonTextClasses}>{renderStyledText(text)}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Main TextCarousel wrapper component
export function TextCarousel({ texts }: TextCarouselProps) {
  return (
    <>
      <TextCarouselMobile texts={texts} />
      <TextCarouselDesktop texts={texts} />
    </>
  );
}
